import { Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { useState } from 'react'
import type { CaseMessage, QueueEmail } from '@/lib/services/caseThreadsService'
import { suggestedRequestLabel } from '@/lib/utils/caseThreads'
import { MessageBubble } from './MessageBubble'
import { ThreadComposer } from './ThreadComposer'

type Props = {
  loading: boolean
  queueEmail: QueueEmail | null
  messages: CaseMessage[]
  lockedSubject: string
  parentTicketId: number | null
  requestNumber?: string | null
  subject?: string | null
  displayStatus?: string | null
  canReply?: boolean
  allowReplyAll?: boolean
  onReply: (body: string, cc: string, replyAll: boolean, attachments: File[]) => Promise<void>
  onNote: (body: string) => Promise<void>
  onWaiting: () => Promise<void>
  onOpenParent: () => void
  onAttach: () => void
  onCreate: () => void
  onIgnore: () => Promise<void>
}

export function CommunicationPane({
  loading,
  queueEmail,
  messages,
  lockedSubject,
  parentTicketId,
  requestNumber,
  subject,
  displayStatus,
  canReply = true,
  allowReplyAll = true,
  onReply,
  onNote,
  onWaiting,
  onOpenParent,
  onAttach,
  onCreate,
  onIgnore,
}: Props) {
  const [replyOpen, setReplyOpen] = useState(false)
  if (loading) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    )
  }

  if (queueEmail) {
    const suggestion = suggestedRequestLabel(queueEmail.suggested_request_number)
    return (
      <VStack align="stretch" h="100%" spacing={0}>
        <Box p={4} borderBottomWidth="1px">
          <Text fontWeight="bold">{queueEmail.subject || '(No subject)'}</Text>
          <Text fontSize="sm" color="gray.500">
            {queueEmail.from_name || queueEmail.from_email}
          </Text>
          {suggestion ? <Badge colorScheme="orange">{suggestion}</Badge> : null}
          <Text mt={3} whiteSpace="pre-wrap">
            {queueEmail.body_preview}
          </Text>
          <HStack mt={4}>
            <Button size="sm" onClick={onCreate}>
              Create Ticket Request
            </Button>
            <Button size="sm" variant="outline" onClick={onAttach}>
              Attach to existing
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void onIgnore()}>
              Ignore
            </Button>
          </HStack>
        </Box>
      </VStack>
    )
  }

  if (!requestNumber && messages.length === 0) {
    return (
      <Text p={4} color="gray.500">
        Select a ticket request to read and reply.
      </Text>
    )
  }

  return (
    <VStack align="stretch" h="100%" spacing={0}>
      <HStack p={3} borderBottomWidth="1px" justify="space-between" align="flex-start">
        <Box minW={0}>
          <Text fontWeight="semibold">{requestNumber}</Text>
          {subject ? (
            <Text fontSize="sm" color="gray.600" noOfLines={2}>
              {subject}
            </Text>
          ) : null}
        </Box>
        <HStack>
          {displayStatus ? <Badge textTransform="none">{displayStatus}</Badge> : null}
          <Button size="xs" colorScheme="blue" onClick={() => setReplyOpen(true)}>
            {canReply ? 'Reply' : 'Note'}
          </Button>
          <Button size="xs" variant="outline" onClick={() => void onWaiting()}>
            Mark waiting
          </Button>
          {parentTicketId ? (
            <Button size="xs" variant="outline" onClick={onOpenParent}>
              Open parent
            </Button>
          ) : (
            <Text fontSize="xs" color="gray.500">
              No parent ticket yet
            </Text>
          )}
        </HStack>
      </HStack>
      <VStack align="stretch" flex="1" overflowY="auto" p={3} spacing={3}>
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </VStack>
      <Drawer isOpen={replyOpen} placement="right" size="md" onClose={() => setReplyOpen(false)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Reply</DrawerHeader>
          <DrawerBody>
            <ThreadComposer
              lockedSubject={lockedSubject}
              canReply={canReply}
              allowReplyAll={allowReplyAll && canReply}
              onReply={async (body, cc, replyAll, attachments) => {
                await onReply(body, cc, replyAll, attachments)
                setReplyOpen(false)
              }}
              onNote={async (body) => {
                await onNote(body)
                setReplyOpen(false)
              }}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  )
}
